import { IconX, IconUserPlus, IconUser, IconTag, IconClock, IconMessageDots, IconFileDescription } from "@tabler/icons-react";
import { ReactNode, useState } from "react";
import {
  useFetchAdminTicketDetails,
  useCloseAdminTicket,
  useReopenAdminTicket,
  useAddAdminTicketComment,
  useUpdateAdminTicket,
  useAssignAdminTicket
} from "../../../apis/ticketManagementClientApis";
import { useFetchStaffsAllData } from "../../../apis/staffApis";
import moment from "moment";
import ButtonStyled from "../../../components/common/ButtonStyled";
import TextareaField from "../../../components/common/TextareaField";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
  ticketId: string;
};

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => {
  const { t } = useLanguage();
  return (
    <div className="flex justify-between items-center w-full">
      <div className="w-full">
        <h2 className="capitalize text-[#212121] text-sm font-medium">{t(label)}</h2>
      </div>
      <div className="w-full text-end">
        {typeof value === "string" ? (
          <p className="text-[#1E293B] text-base">{value}</p>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

const AdminViewModal = ({ open, onClose, ticketId }: Props) => {
  const { t } = useLanguage();
  const { data: ticket, isLoading } = useFetchAdminTicketDetails(ticketId);
  const { mutate: closeTicket, isPending: closing } = useCloseAdminTicket();
  const { mutate: reopenTicket, isPending: reopening } = useReopenAdminTicket();
  const { mutate: addComment, isPending: addingComment } = useAddAdminTicketComment();
  const { mutate: updateTicket, isPending: updating } = useUpdateAdminTicket();
  const { mutate: assignTicket, isPending: assigning } = useAssignAdminTicket();
  const { data: staffs } = useFetchStaffsAllData();
  
  const profile = useSelector((state: any) => state.auth?.profile);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { message: "" }
  });

  const [showCommentBox, setShowCommentBox] = useState(false);

  const [activeTab, setActiveTab] = useState<"COMMENTS" | "ACTIVITY">("COMMENTS");

  if (!open) return null;

  const onCommentSubmit = (data: { message: string }) => {
    addComment({ ticketId, data: { message: data.message, attachmentUrls: [] } }, {
      onSuccess: () => {
        reset();
        setShowCommentBox(false);
      }
    });
  };

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#F7F6F2] rounded-2xl w-[655px] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#d2d2d2]">
          <h1 className="text-2xl">
            {t("View Ticket")} {ticket ? `- ${ticket.ticketNumber || ticket.shortTicketId || ticket.ticketId}` : ""}
          </h1>
          <button
            onClick={onClose}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer"
          >
            <IconX size={20} />
          </button>
        </div>

        <div className="w-full overflow-y-auto p-4 space-y-4 flex-1">
          {isLoading ? (
            <div className="text-center py-4">{t("Loading details...")}</div>
          ) : ticket ? (
            <>
              <InfoRow label="Subject" value={ticket.subject || ticket.title} />
              <InfoRow label="Category Type" value={t((ticket.type || ticket.category)?.replace("_", " "))} />
              <InfoRow label="Date" value={moment(ticket.createdAt).format("DD-MMM-YYYY, h:mm A")} />
              <div className="flex justify-between items-center w-full">
                <div className="w-full">
                  <h2 className="capitalize text-[#212121] text-sm font-medium">
                    {t("Status")}
                  </h2>
                </div>

                <div className="w-full text-end flex justify-end">
                  <select
                    className={`rounded-full w-36 px-4 py-1 text-center font-medium focus:outline-none cursor-pointer transition-all ${
                      ticket.status === 'RESOLVED' || ticket.status === 'CLOSED' ? "bg-[#E6F7E8] border border-[#20BF55] text-[#212121]" :
                      ticket.status === 'OPEN' || ticket.status === 'REOPENED' ? "bg-[#FF4848]/10 border border-[#EC221F] text-[#FF4848]" :
                      "bg-[#FCB910]/10 border border-[#FCB910] text-[#FCB910]"
                    }`}
                    value={ticket.status}
                    onChange={(e) => updateTicket({ ticketId, data: { status: e.target.value } })}
                    disabled={updating}
                  >
                    <option value="OPEN">{t("OPEN")}</option>
                    <option value="IN_PROGRESS">{t("IN PROGRESS")}</option>
                    <option value="ON_HOLD">{t("ON HOLD")}</option>
                    <option value="RESOLVED">{t("RESOLVED")}</option>
                    <option value="CLOSED">{t("CLOSED")}</option>
                    <option value="REOPENED">{t("REOPENED")}</option>
                  </select>
                </div>
              </div>

              {/* Status & Info Grid */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <IconUser size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-[#64748B] text-xs font-semibold uppercase tracking-wider">{t("Assigned Staff")}</h2>
                      <p className="text-[#1E293B] text-sm font-bold">
                        {ticket.assignedToName || ticket.assignedTo ? (ticket.assignedToName || t("Assigned")) : t("Unassigned")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <IconTag size={20} className="text-indigo-600" />
                    </div>
                    <div>
                      <h2 className="text-[#64748B] text-xs font-semibold uppercase tracking-wider">{t("Ticket Priority")}</h2>
                      <p className="text-[#1E293B] text-sm font-bold">{t(ticket.priority)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="mt-4 p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <IconFileDescription size={18} className="text-gray-400" />
                  <h3 className="font-bold text-[#1E293B]">{t("Detailed Description")}</h3>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <p className="text-[#475569] text-sm leading-relaxed whitespace-pre-wrap">{ticket.description}</p>
                </div>
              </div>

              {ticket.attachmentUrls && ticket.attachmentUrls.length > 0 && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                  <h3 className="font-semibold mb-2">{t("Attachments")}</h3>
                  <div className="flex flex-wrap gap-4 mt-2">
                    {ticket.attachmentUrls.map((url: string, index: number) => {
                      const isImage = url.match(/\.(jpeg|jpg|gif|png)$/i) || url.startsWith('blob:');
                      let fileName = url.substring(url.lastIndexOf("/") + 1);
                      if (!fileName || url.startsWith('blob:')) {
                        fileName = `${t("Attachment")} ${index + 1}`;
                      }
                      
                      return (
                        <div key={index} className="flex flex-col items-center gap-2">
                          {isImage ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity border border-[#D2D2D2] rounded-xl overflow-hidden shadow-sm">
                              <img src={url} alt={fileName} className="w-24 h-24 object-cover" />
                            </a>
                          ) : (
                            <a 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-[#F2F2F7] px-4 py-3 border border-[#D2D2D2] rounded-xl hover:bg-gray-200 transition-colors shadow-sm"
                            >
                              <span className="text-sm text-[#212121] truncate max-w-[150px] font-medium">{fileName}</span>
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tabs for Comments and Activity Logs */}
              <div className="mt-6 border-t pt-4">
                <div className="flex gap-4 mb-4">
                  <button 
                    onClick={() => setActiveTab("COMMENTS")}
                    className={`font-semibold text-lg pb-1 border-b-2 ${activeTab === "COMMENTS" ? "border-[#FBD037] text-black" : "border-transparent text-gray-500"}`}
                  >
                    {t("Communication")}
                  </button>
                  <button 
                    onClick={() => setActiveTab("ACTIVITY")}
                    className={`font-semibold text-lg pb-1 border-b-2 ${activeTab === "ACTIVITY" ? "border-[#FBD037] text-black" : "border-transparent text-gray-500"}`}
                  >
                    {t("Activity Log")}
                  </button>
                </div>
                
                {activeTab === "COMMENTS" ? (
                  <>
                    {ticket.comments && ticket.comments.length > 0 ? (
                      <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                        {ticket.comments.map((comment: any) => (
                          <div key={comment.id} className={`p-3 rounded-lg ${comment.authorType === 'CUSTOMER' || comment.authorType === 'MERCHANT' ? 'bg-blue-50 ml-auto border border-blue-100' : 'bg-gray-100 mr-auto border border-gray-200'} w-[85%]`}>
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-xs text-gray-600">{t(comment.authorType?.toLowerCase())}</span>
                              <span className="text-xs text-gray-500">{moment(comment.createdAt).format("DD-MMM HH:mm")}</span>
                            </div>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">{t("No comments yet")}</p>
                    )}
                    
                    {ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && (
                      <div className="mt-4">
                        {showCommentBox ? (
                          <form onSubmit={handleSubmit(onCommentSubmit)} className="space-y-2">
                            <TextareaField
                              label={t("Message")}
                              name="message"
                              register={register}
                              validation={{ required: t("Message is required") }}
                              error={errors.message?.message as string}
                              placeholder={t("Type your reply here...")}
                            />
                            <div className="flex justify-end gap-2">
                              <button 
                                type="button" 
                                onClick={() => setShowCommentBox(false)}
                                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg"
                              >
                                {t("Cancel")}
                              </button>
                              <ButtonStyled type="submit" value={t("Send")} isPending={addingComment} />
                            </div>
                          </form>
                        ) : (
                          <button 
                            onClick={() => setShowCommentBox(true)}
                            className="w-full py-2 border border-dashed border-gray-400 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            + {t("Add Reply")}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                    {ticket.activityLogs && ticket.activityLogs.length > 0 ? (
                      ticket.activityLogs.map((log: any) => (
                        <div key={log.id} className="flex gap-3 text-sm">
                          <div className="min-w-[120px] text-gray-500 text-xs mt-1">
                            {moment(log.createdAt).format("DD-MMM HH:mm")}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">
                              {log.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              By {log.performedByType}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">{t("No activity logs yet")}</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-4 text-red-500">{t("Failed to load ticket details.")}</div>
          )}
        </div>

        <div className="border-t border-[#D8D8D8] px-4 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-2xl">
          {ticket && (
            <>
              {/* Admin or Assigned LM Staff can solve */}
              {(profile?.type === "superAdmin" || profile?.type === "staff" || profile?.type === "admin" || profile?.type === "client" || ticket.assignedToLmStaffId === profile?.id) && (
                <>

                  {ticket.status === "CLOSED" ? (
                    <ButtonStyled
                      value={t("Reopen Ticket")}
                      onClick={() => reopenTicket({ ticketId, reason: "Reopened by admin/staff" })}
                      isPending={reopening}
                      extraClasses="!bg-blue-500 hover:!bg-blue-600 !text-white"
                    />
                  ) : (
                    <ButtonStyled
                      value={t("Close Ticket")}
                      onClick={() => closeTicket(ticketId)}
                      isPending={closing}
                      extraClasses="!bg-red-500 hover:!bg-red-600 !text-white"
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminViewModal;
